import React, { useState, useEffect, useRef } from 'react'
import propTypes from 'prop-types'

// This is used to open and close the left drawer
// We only use it for table of contents right now
// so I'm putting it here but we may want to move
// it somewhere else. A localstorage key is required
// to handle whether the drawer should be open or not
// and since we use different drawers in the reader
// and elsewhere, this component needs to be a little
// angnostic. This button should be used in a react
// portal that controls the opening/closing of the
// drawer that holds the table of contents content.
// This button is purposely not a child of the of the
// table of contents app, it's more like a cousin.

// It can control both the persistent reader drawer (useLocalStorage = true)
// and the overlay drawer (useLocalStorage = false)
export const NavbarButton = ({ drawerKey, useLocalStorage = false }) => {
  const [expanded, setExpanded] = useState(() => {
    if (useLocalStorage) {
      const stored = localStorage.getItem(drawerKey)
      return stored === null ? true : stored === 'true'
    }
    return false
  })

  // tracks whether user clicked the toc button or just hovered
  const [lockedOpen, setLockedOpen] = useState(() => {
    if (useLocalStorage) {
      const stored = localStorage.getItem(drawerKey)
      return stored === null ? true : stored === 'true'
    }
    return false
  })

  const hoverTimeout = useRef(null)
  const isHoveringButton = useRef(false)
  const isHoveringDrawer = useRef(false)
  const justCollapsed = useRef(false)

  // Sync with localStorage
  useEffect(() => {
    if (useLocalStorage) {
      localStorage.setItem(drawerKey, lockedOpen)
    }
  }, [lockedOpen, drawerKey, useLocalStorage])

  // Listen for global open/close events
  useEffect(() => {
    const handleOpen = () => {
      setExpanded(true)
    }
    const handleClose = () => {
      setExpanded(false)
      setLockedOpen(false)
    }

    window.addEventListener('open-left-drawer', handleOpen)
    window.addEventListener('close-left-drawer', handleClose)

    return () => {
      window.removeEventListener('open-left-drawer', handleOpen)
      window.removeEventListener('close-left-drawer', handleClose)
    }
  }, [])

  useEffect(() => {
    const handleLock = () => {
      if (!lockedOpen) {
        setLockedOpen(true)
        window.dispatchEvent(new CustomEvent('open-left-drawer'))
      }
    }

    window.addEventListener('lock-left-drawer', handleLock)
    return () => window.removeEventListener('lock-left-drawer', handleLock)
  }, [lockedOpen])

  // Listen for localStorage changes
  useEffect(() => {
    if (!useLocalStorage) return
    const handleStorage = e => {
      if (e.key === drawerKey) {
        setExpanded(e.newValue === 'true')
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [drawerKey, useLocalStorage])

  // --- CLICK HANDLER ---
  const toggleLeftDrawer = e => {
    // Skip onClick if it was triggered by keyboard (Enter/Space)
    // because onKeyDown will handle it
    if (e.detail === 0) {
      // detail === 0 means keyboard triggered the click
      return
    }

    e.preventDefault()
    e.stopPropagation()

    if (lockedOpen) {
      justCollapsed.current = true
      window.dispatchEvent(
        new CustomEvent('close-left-drawer', {
          detail: { restoreFocus: true },
        })
      )
      setLockedOpen(false)
    } else if (expanded) {
      setLockedOpen(true)
      window.dispatchEvent(
        new CustomEvent('open-left-drawer', {
          detail: { triggeredBy: 'click' },
        })
      )
    } else {
      window.dispatchEvent(
        new CustomEvent('open-left-drawer', {
          detail: { triggeredBy: 'click' },
        })
      )
      setLockedOpen(true)
    }
  }

  // --- Begin hover handlers ---
  const handleMouseEnter = () => {
    if (lockedOpen) return
    if (justCollapsed.current) {
      // Consume the guard so it only blocks the one reopen right after a
      // click-to-collapse, not any later legitimate hover.
      justCollapsed.current = false
      return
    }
    isHoveringButton.current = true // Add this
    clearTimeout(hoverTimeout.current)
    window.dispatchEvent(new CustomEvent('open-left-drawer'))
  }

  const handleMouseLeave = () => {
    justCollapsed.current = false
    if (lockedOpen) return
    isHoveringButton.current = false // Add this
    hoverTimeout.current = setTimeout(() => {
      // Only close if not hovering over drawer either
      if (!isHoveringButton.current && !isHoveringDrawer.current) {
        window.dispatchEvent(
          new CustomEvent('close-left-drawer', {
            detail: { restoreFocus: false },
          })
        )
      }
    }, 50)
  }
  // --- End hover handlers ---

  // Listen for when the user leaves the drawer area too
  useEffect(() => {
    const drawer = document.getElementById('left-drawer')
    if (!drawer) return

    const handleDrawerEnter = () => {
      isHoveringDrawer.current = true
      clearTimeout(hoverTimeout.current)
    }
    const handleDrawerLeave = () => {
      if (lockedOpen) return
      isHoveringDrawer.current = false // Add this
      hoverTimeout.current = setTimeout(() => {
        // Only close if not hovering over button either
        if (!isHoveringButton.current && !isHoveringDrawer.current) {
          window.dispatchEvent(
            new CustomEvent('close-left-drawer', {
              detail: { restoreFocus: false },
            })
          )
        }
      }, 50)
    }

    drawer.addEventListener('mouseenter', handleDrawerEnter)
    drawer.addEventListener('mouseleave', handleDrawerLeave)

    return () => {
      drawer.removeEventListener('mouseenter', handleDrawerEnter)
      drawer.removeEventListener('mouseleave', handleDrawerLeave)
    }
  }, [lockedOpen])

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()

      // Call toggleLeftDrawer directly without going through click
      if (lockedOpen) {
        window.dispatchEvent(
          new CustomEvent('close-left-drawer', {
            detail: { restoreFocus: true },
          })
        )
        setLockedOpen(false)
      } else if (expanded) {
        setLockedOpen(true)
        window.dispatchEvent(
          new CustomEvent('open-left-drawer', {
            detail: { triggeredBy: 'keyboard' },
          })
        )
      } else {
        window.dispatchEvent(
          new CustomEvent('open-left-drawer', {
            detail: { triggeredBy: 'keyboard' },
          })
        )
        setLockedOpen(true)
      }
    }
  }

  return (
    <button
      type="button"
      className="me-btn-icon me-btn-icon-neutral me-btn-icon-disclosure"
      data-me-hover-fill="glide"
      aria-label="Table of contents"
      id="navMenu"
      aria-expanded={!!expanded}
      aria-controls="left-drawer"
      data-locked={lockedOpen}
      tabIndex="0"
      onClick={toggleLeftDrawer}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {lockedOpen ? (
        <i
          className="me-btn-glyph fa-kit fa-left-solid-panel-rectangle"
          aria-hidden="true"
        />
      ) : (
        <i
          className="me-btn-glyph fa-kit fa-left-panel-rectangle"
          aria-hidden="true"
        />
      )}
    </button>
  )
}

NavbarButton.propTypes = {
  drawerKey: propTypes.string,
  useLocalStorage: propTypes.bool,
  onKeyDown: propTypes.func,
}
